import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import atelier2Service from '../services/atelier2Service';

const useAtelier2Store = create(
  subscribeWithSelector((set, get) => ({
    isLoading: false,
    error: null,
    sourcesRisque: [],
    couplesSROV: [],
    selectedCouplesSROV: [],
    isDataLoaded: false,
    analysisId: null,
    customCategories: [],
    threatCategoriesSelected: [],
    hiddenCategories: [],

    setSourcesRisque: (sourcesOrUpdater) => {
      if (typeof sourcesOrUpdater === 'function') {
        set((state) => ({ sourcesRisque: sourcesOrUpdater(state.sourcesRisque) }));
      } else {
        set({ sourcesRisque: sourcesOrUpdater });
      }
    },

    setCustomCategories: (customOrUpdater) => {
      if (typeof customOrUpdater === 'function') {
        set((state) => ({ customCategories: customOrUpdater(state.customCategories) }));
      } else {
        set({ customCategories: customOrUpdater });
      }
    },

    setThreatCategoriesSelected: (selectedOrUpdater) => {
      if (typeof selectedOrUpdater === 'function') {
        set((state) => ({ threatCategoriesSelected: selectedOrUpdater(state.threatCategoriesSelected) }));
      } else {
        set({ threatCategoriesSelected: selectedOrUpdater });
      }
    },

    setHiddenCategories: (hiddenOrUpdater) => {
      if (typeof hiddenOrUpdater === 'function') {
        set((state) => ({ hiddenCategories: hiddenOrUpdater(state.hiddenCategories) }));
      } else {
        set({ hiddenCategories: hiddenOrUpdater });
      }
    },

    getThreatCategories: async (analysisId) => {
      try {
        return await atelier2Service.getThreatCategories(analysisId);
      } catch (error) {
        console.error('[useAtelier2Store] Error loading threat categories');
        return { definitions: { builtIns: [], custom: [] }, selected: [], hidden: [] };
      }
    },

    loadThreatCategories: async (analysisId) => {
      if (!analysisId) return;
      try {
        const data = await atelier2Service.getThreatCategories(analysisId);
        set({
          customCategories: data.definitions?.custom || [],
          threatCategoriesSelected: data.selected || [],
          hiddenCategories: data.hidden || []
        });
        return data;
      } catch (error) {
        console.error('[useAtelier2Store] Error loading threat categories into store');
        return { definitions: { builtIns: [], custom: [] }, selected: [], hidden: [] };
      }
    },

    saveThreatCategories: async (analysisId, data, onBroadcast) => {
      try {
        const result = await atelier2Service.saveThreatCategories(analysisId, data);
        if (result?.success) {
          set({
            customCategories: data.customCategories || [],
            threatCategoriesSelected: data.selected || [],
            hiddenCategories: data.hidden || []
          });
          if (onBroadcast) onBroadcast('atelier2-activite1');
        }
        return result;
      } catch (error) {
        console.error('[useAtelier2Store] Error saving threat categories:', error);
        return { success: false, message: error.message };
      }
    },

    getSourcesDeRisque: async (analysisId) => {
      try {
        if (!analysisId) {
          console.error('[useAtelier2Store] Invalid analysis ID provided to getSourcesDeRisque');
          return [];
        }
        const result = await atelier2Service.getSourcesRisque(analysisId);
        if (!result || !Array.isArray(result)) {
          return [];
        }
        return result;
      } catch (error) {
        console.error('[useAtelier2Store] Error loading sources de risque:', error.message);
        return [];
      }
    },

    saveSourcesDeRisque: async (analysisId, data, radarImageData = null, onBroadcast) => {
      try {
        if (!analysisId) {
          console.error('[useAtelier2Store] Invalid analysis ID provided to saveSourcesDeRisque');
          return { success: false, message: 'Invalid analysis ID' };
        }
        const result = await atelier2Service.saveSourcesRisque(analysisId, data, radarImageData);
        if (result.success) {
          set({ sourcesRisque: data });
          if (onBroadcast) onBroadcast('atelier2-activite1');
        }
        return result;
      } catch (error) {
        console.error('[useAtelier2Store] Error saving sources de risque:', error.message);
        return { success: false, message: error.message };
      }
    },

    getCouplesSROV: async (analysisId) => {
      try {
        return await atelier2Service.getCouplesSROV(analysisId);
      } catch (error) {
        console.error('[useAtelier2Store] Error loading couples SR/OV');
        return [];
      }
    },

    saveCouplesSROV: async (analysisId, data, onBroadcast) => {
      try {
        const result = await atelier2Service.saveCouplesSROV(analysisId, data);
        if (result.success) {
          set({ couplesSROV: data });
          if (onBroadcast) onBroadcast('atelier2-activite3');
        }
        return result;
      } catch (error) {
        console.error('[useAtelier2Store] Error saving couples SR/OV');
        return { success: false, message: error.message };
      }
    },

    getSelectedCouplesSROV: async (analysisId) => {
      try {
        return await atelier2Service.getSelectedCouplesSROV(analysisId);
      } catch (error) {
        console.error('[useAtelier2Store] Error loading selected couples SR/OV');
        return [];
      }
    },

    saveSelectedCouplesSROV: async (analysisId, data, onBroadcast) => {
      try {
        const result = await atelier2Service.saveSelectedCouplesSROV(analysisId, data);
        if (result.success) {
          set({ selectedCouplesSROV: data });
          if (onBroadcast) onBroadcast('atelier2-activite3');
        }
        return result;
      } catch (error) {
        console.error('[useAtelier2Store] Error saving selected couples SR/OV');
        return { success: false, message: error.message };
      }
    },

    getRadarVisualization: async (analysisId) => {
      try {
        return await atelier2Service.getRadarVisualization(analysisId);
      } catch (error) {
        console.error('[useAtelier2Store] Error loading radar visualization');
        return null;
      }
    },

    loadData: async (analysisId) => {
      if (!analysisId) return;
      set({ isLoading: true, error: null, analysisId });

      try {
        const promises = [
          atelier2Service.getSourcesRisque(analysisId),
          atelier2Service.getCouplesSROV(analysisId),
          atelier2Service.getSelectedCouplesSROV(analysisId),
        ];

        const results = await Promise.allSettled(promises);
        const [sourcesRes, couplesRes, selectedCouplesRes] = results;
        const errors = {};

        let sourcesRisque = [];
        if (sourcesRes.status === 'fulfilled' && Array.isArray(sourcesRes.value)) {
          sourcesRisque = sourcesRes.value;
        } else {
          errors.sourcesRisque = sourcesRes.reason?.message || 'Failed to load sources de risque';
        }

        let couplesSROV = [];
        if (couplesRes.status === 'fulfilled' && Array.isArray(couplesRes.value)) {
          couplesSROV = couplesRes.value;
        } else {
          errors.couplesSROV = couplesRes.reason?.message || 'Failed to load couples SR/OV';
        }

        let selectedCouplesSROV = [];
        if (selectedCouplesRes.status === 'fulfilled' && Array.isArray(selectedCouplesRes.value)) {
          selectedCouplesSROV = selectedCouplesRes.value;
        } else {
          errors.selectedCouplesSROV = selectedCouplesRes.reason?.message || 'Failed to load selected couples SR/OV';
        }

        if (analysisId !== get().analysisId) return;
        set({
          sourcesRisque,
          couplesSROV,
          selectedCouplesSROV,
          isDataLoaded: true,
          isLoading: false,
          error: Object.keys(errors).length > 0 ? errors : null,
        });
      } catch (error) {
        console.error('[useAtelier2Store] Error loading data:', error);
        set({ isLoading: false, error: { general: error.message } });
      }
    },

    refreshSlice: async (analysisId, tab) => {
      const sliceMap = {
        'atelier2-activite1': async () => {
          const result = await atelier2Service.getSourcesRisque(analysisId);
          if (Array.isArray(result)) set({ sourcesRisque: result });
        },
        'atelier2-activite2': async () => {
          const result = await atelier2Service.getSourcesRisque(analysisId);
          if (Array.isArray(result)) set({ sourcesRisque: result });
        },
        'atelier2-activite3': async () => {
          const [couplesRes, selectedRes] = await Promise.allSettled([
            atelier2Service.getCouplesSROV(analysisId),
            atelier2Service.getSelectedCouplesSROV(analysisId),
          ]);
          if (couplesRes.status === 'fulfilled' && Array.isArray(couplesRes.value)) {
            set({ couplesSROV: couplesRes.value });
          }
          if (selectedRes.status === 'fulfilled' && Array.isArray(selectedRes.value)) {
            set({ selectedCouplesSROV: selectedRes.value });
          }
        },
      };
      const fn = sliceMap[tab];
      if (fn) {
        try { await fn(); }
        catch (e) { console.error(`[A2Store] refreshSlice(${tab}) failed:`, e); }
      }
    },

    resetState: () => {
      set({
        isLoading: false,
        error: null,
        sourcesRisque: [],
        couplesSROV: [],
        selectedCouplesSROV: [],
        customCategories: [],
        threatCategoriesSelected: [],
        hiddenCategories: [],
        isDataLoaded: false,
        analysisId: null,
      });
    },
  }))
);

export default useAtelier2Store;